import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  CreditCard,
  Edit2,
  Loader2,
  UserCheck,
  WalletCardsIcon,
} from "lucide-react";
import {
  canModifyRole,
  formatCurrency,
  formatDate,
  formatUSPhone,
  intervalToCurrentPeriod,
} from "shared";
import { GivefrontCardIcon } from "@/components/icons/credit-card-icon";
import { RecentTransactionDisplay } from "@/components/RecentTransactionDisplay";
import { SpendFlexibilityDetails } from "@/components/spend-flexibility-details";
import { RenderStatusBadge } from "@/components/table/cells/RenderStatusBadge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { UserAvatar } from "@/components/user-avatar";
import { useRequiredRole } from "@/hooks/useRequiredRole";
import { restrictRoute } from "@/lib/routeBlocker";
import { DeactivateAccountButton } from "./-components/deactivate-account";

export const Route = createFileRoute(
  "/_authed/_org/organization/people/all/$id/",
)({
  beforeLoad: async ({ context }) =>
    restrictRoute(context, {
      redirect: {
        to: "/organization/people",
      },
      requiredRole: "admin",
    }),
  component: RouteComponent,
});

function RouteComponent() {
  const { trpc, queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();
  const { admin, owner } = useRequiredRole();
  const { data: user } = useSuspenseQuery(
    trpc.org.orgUser.queryOptions({
      userId: id,
    }),
  );

  const { mutate: activateUser, isPending: isActivating } = useMutation(
    trpc.org.updateRole.mutationOptions({
      async onSuccess() {
        queryClient.invalidateQueries(trpc.org.users.queryOptions());
        await queryClient.refetchQueries(
          trpc.org.orgUser.queryOptions({ userId: id }),
        );
      },
    }),
  );

  return (
    <div className="flex h-screen flex-1 flex-col py-6">
      {/* User Info Card */}
      <div className="flex items-center gap-4 border-border border-b px-4 pb-6">
        <UserAvatar
          userId={user.id}
          includeName={false}
          className="text-2xl! [&>span]:size-16! [&_svg]:size-8"
        />

        <div className="flex-1">
          <CardTitle className="text-2xl">{user.name}</CardTitle>
          <CardDescription className="mt-1">
            {user.email} / {formatUSPhone(user.phone)}
          </CardDescription>
          <div className="mt-2 flex items-center gap-4">
            <RenderStatusBadge status={user.role}>
              {user.role === "none" ? "Deactivated" : user.role}
            </RenderStatusBadge>

            <span className="text-muted-foreground text-sm">
              Joined {formatDate(user.createdAt)}
            </span>
          </div>
        </div>
      </div>

      {admin && user.role === "none" && (
        <div className="border-border border-b px-6 py-4">
          <Button
            size="sm"
            variant="outline"
            className="w-full!"
            onClick={() =>
              activateUser({ memberId: user.memberId, newRole: "member" })
            }
          >
            {isActivating ? (
              <Loader2 className="size-3.5 animate-spin text-neutral-500" />
            ) : (
              <UserCheck className="size-3.5 text-neutral-500" />
            )}
            Reactivate User
          </Button>
        </div>
      )}

      {user.role !== "owner" &&
        user.role !== "none" &&
        canModifyRole({
          admin,
          owner,
          toModifyRole: user.role,
        }) && (
          <div className="flex gap-4 border-border border-b px-6 py-4">
            <Link
              to="/organization/people/all/$id/edit-role"
              params={{ id: user.id }}
              className="grow"
            >
              <Button size="sm" variant="outline" className="w-full!">
                <Edit2 className="size-3.5 text-neutral-500" />
                Edit Role
              </Button>
            </Link>

            <DeactivateAccountButton user={user} className="max-w-1/2 grow" />
          </div>
        )}

      {/* Main Content with Accordion Sections */}
      <Accordion
        type="multiple"
        defaultValue={["cards", "transactions"]}
        className="mt-4 space-y-4 overflow-y-auto px-4"
      >
        {/* Cards Section */}
        {user.role !== "none" && (
          <AccordionItem value="cards" className="border-b-0">
            <AccordionTrigger className="font-semibold text-lg">
              Cards ({user.cards.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-3 pt-2">
                {user.cards.length === 0 ? (
                  <Card>
                    <CardContent className="mt-0! flex items-center justify-center gap-1 py-2">
                      <WalletCardsIcon
                        strokeWidth={1.25}
                        className="size-16 text-neutral-300"
                      />
                      <div className="ml-2 flex flex-col gap-1">
                        <Text
                          variant={"large"}
                          className="font-semibold text-sm"
                        >
                          No cards issued to this user
                        </Text>
                        <Link to="/cards/create" className="w-fit">
                          <Button size="sm" variant="outline">
                            Issue Card
                            <CreditCard
                              className="size-3.5 text-neutral-500"
                              strokeWidth={2.5}
                            />
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="flex flex-col gap-2">
                    {user.cards.map((card) => (
                      <Link
                        key={card.id}
                        to="/cards/$id"
                        params={{
                          id: card.id,
                        }}
                      >
                        <Card className="group rounded-sm border-neutral-200 bg-neutral-50/50 p-4 shadow-none hover:border-neutral-300 hover:bg-neutral-50">
                          <div className="flex flex-row items-center justify-between">
                            <div className="mr-auto flex items-center space-x-2">
                              <GivefrontCardIcon
                                variant={card.type}
                                className="size-10"
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="flex items-center gap-1 font-medium text-sm">
                                    {card.name}{" "}
                                    <ArrowUpRight className="hidden size-3 group-hover:block" />
                                  </span>
                                </div>
                                <div className="text-muted-foreground text-xs">
                                  •••• {card.last4} •{" "}
                                  {intervalToCurrentPeriod(
                                    card.restriction.refreshInterval,
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-row items-center space-x-1">
                              <span className="font-medium text-lg">
                                {formatCurrency(
                                  card.restriction.periodBalanceCents ?? 0,
                                )}
                              </span>
                              <span>{"/"}</span>
                              <span className="item-center flex flex-row font-normal text-base text-muted-foreground">
                                {formatCurrency(
                                  card.restriction.periodTargetCents ?? 0,
                                  false,
                                )}{" "}
                                <SpendFlexibilityDetails
                                  spendLimit={{
                                    ...card.restriction,
                                    balanceCents:
                                      card.restriction.periodBalanceCents,
                                    targetCents:
                                      card.restriction.periodTargetCents,
                                  }}
                                />
                              </span>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {/* Transactions Section */}
        <AccordionItem value="transactions">
          <AccordionTrigger className="font-semibold text-lg">
            Recent Transactions
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-2">
              <RecentTransactionDisplay userId={id} showHeader={false} />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
